import React from "react";
import {
  Box,
  Typography,
  Fade,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ArrowBack, Computer, CameraAlt as CameraIcon, VpnKey, Edit } from "@mui/icons-material";
import { Trash2, Download, Lock } from "lucide-react";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { MobileSortControl } from "../../shared/components/MobileSortControl";
import { EmptyState } from "../../shared/components/EmptyState";
import { formatToIST } from "../../utils/formatting";
import { sortData } from "../../utils/sorting";
import { headerAccentGradient } from "../../theme/theme";

function ProjectDetailsView({
  darkMode,
  theme,
  selectedProject,
  setActiveView,
  setSelectedProject,
  setSearchTerm,
  loading,
  systems,
  cameras,
  licenses,
  visibleColumns,
  sortConfig,
  handleSort,
  currentUser,
  isMobile,
  handleEditSystem,
  deleteSystem,
  handleEditCamera,
  deleteCamera,
  downloadLicenseFile,
  generateOfflineKey,
  deleteLicense,
  fetchSystemOptions,
  setNewLicense,
  setSystemSearch,
  setOpenDialog,
}) {
  const projectSystems = sortData(
    systems.filter((system) => system.project_id === selectedProject),
    sortConfig.systems,
    systems
  );
  const projectCameras = sortData(
    cameras.filter((camera) =>
      systems.some((system) => system.uuid === camera.uuid && system.project_id === selectedProject)
    ),
    sortConfig.cameras,
    systems
  );
  const projectLicenses = sortData(
    licenses.filter((license) =>
      systems.some((system) => system.uuid === license.uuid && system.project_id === selectedProject)
    ),
    sortConfig.licenses,
    systems
  );

  return (
    <Fade in={true} timeout={500}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => {
              setActiveView("projects");
              setSelectedProject(null);
              setSearchTerm("");
            }}
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.5),
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              }
            }}
          >
            Back to Projects
          </Button>
          <Box>
            <Box sx={{ width: 44, height: 4, borderRadius: 2, background: headerAccentGradient, mb: 1.5 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
              Project: {selectedProject}
            </Typography>
            <Typography variant="body2" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
              Detailed view of project resources and licenses
            </Typography>
          </Box>
        </Box>

        {loading.systems || loading.cameras || loading.licenses ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Systems Section */}
            <Grid item xs={12}>
              <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Computer color={theme.palette.primary.main} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                        Systems
                      </Typography>
                    </Box>
                    <Chip
                      label={`${projectSystems.length} Total`}
                      size="small"
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }}
                    />
                  </Box>

                  {isMobile ? (
                    <Box>
                      <MobileSortControl
                        tableType="systems"
                        currentConfig={sortConfig.systems}
                        handleSort={handleSort}
                        options={[
                          { value: 'uuid', label: 'System UID' },
                          { value: 'software_id', label: 'Software ID' },
                          { value: 'amc', label: 'AMC Status' },
                          { value: 'registered_at', label: 'Registered At' },
                        ]}
                      />
                      {projectSystems.map((system) => (
                        <Box
                          key={system.uuid}
                          sx={{
                            p: 2, mb: 1.5, borderRadius: 2,
                            bgcolor: darkMode ? '#16213A' : '#EAF3FB',
                            border: darkMode ? '1px solid #24304A' : '1px solid #D8E1E8',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                              {system.uuid.substring(0, 16)}...
                            </Typography>
                            <Chip
                              size="small"
                              label={system.amc ? "AMC" : "No AMC"}
                              sx={{
                                bgcolor: system.amc ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                                color: system.amc ? 'success.main' : (darkMode ? '#93A2BC' : 'text.secondary'),
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                            <Chip size="small" label={system.software_id || 'N/A'} sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main', fontWeight: 600 }} />
                          </Box>
                          <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#93A2BC' : 'text.secondary', mb: 1.5 }}>
                            Registered {formatToIST(system.registered_at)}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton
                              color="success"
                              size="small"
                              onClick={async () => {
                                const systemOptions = await fetchSystemOptions();
                                setNewLicense({ uuid: system.uuid, start_date: '', end_date: '', systemOptions });
                                setSystemSearch(system.uuid);
                                setOpenDialog(prev => ({ ...prev, license: true }));
                              }}
                            >
                              <VpnKey fontSize="small" />
                            </IconButton>
                            <IconButton color="primary" onClick={() => handleEditSystem(system)} size="small">
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton color="error" onClick={() => deleteSystem(system.uuid)} size="small">
                              <Trash2 size={18} />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                      {projectSystems.length === 0 && (
                        <EmptyState darkMode={darkMode} title="No systems found in this project" />
                      )}
                    </Box>
                  ) : (
                    <>
                      <TableContainer>
                        <Table sx={{ minWidth: 800 }}>
                          <TableHead>
                            <TableRow>
                              {visibleColumns.systems.uuid && (
                                <SortableHeader label="System UID" tableType="systems" columnKey="uuid" currentConfig={sortConfig.systems} handleSort={handleSort} />
                              )}
                              {visibleColumns.systems.software_id && (
                                <SortableHeader label="Software ID" tableType="systems" columnKey="software_id" currentConfig={sortConfig.systems} handleSort={handleSort} />
                              )}
                              {visibleColumns.systems.amc && (
                                <SortableHeader label="AMC Status" tableType="systems" columnKey="amc" currentConfig={sortConfig.systems} handleSort={handleSort} />
                              )}
                              {visibleColumns.systems.registered_at && (
                                <SortableHeader label="Registered At" tableType="systems" columnKey="registered_at" currentConfig={sortConfig.systems} handleSort={handleSort} />
                              )}
                              {visibleColumns.systems.actions && (
                                <TableCell align="center">Actions</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {projectSystems.map((system) => (
                              <TableRow key={system.uuid} hover>
                                {visibleColumns.systems.uuid && (
                                  <TableCell>
                                    <Tooltip title={system.uuid}>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                        {system.uuid.substring(0, 8)}...
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                )}
                                {visibleColumns.systems.software_id && (
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={system.software_id || 'N/A'}
                                      sx={{
                                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                        color: 'secondary.main',
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                )}
                                {visibleColumns.systems.amc && (
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={system.amc ? "Active" : "Inactive"}
                                      sx={{
                                        bgcolor: system.amc ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                                        color: system.amc ? 'success.main' : (darkMode ? '#93A2BC' : 'text.secondary'),
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                )}
                                {visibleColumns.systems.registered_at && (
                                  <TableCell>
                                    <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                                      {formatToIST(system.registered_at)}
                                    </Typography>
                                  </TableCell>
                                )}
                                {visibleColumns.systems.actions && (
                                  <TableCell align="center">
                                    <Tooltip title="Add License for this System">
                                      <IconButton
                                        color="success"
                                        size="small"
                                        sx={{ mr: 1 }}
                                        onClick={async () => {
                                          const systemOptions = await fetchSystemOptions();
                                          setNewLicense({ uuid: system.uuid, start_date: '', end_date: '', systemOptions });
                                          setSystemSearch(system.uuid);
                                          setOpenDialog(prev => ({ ...prev, license: true }));
                                        }}
                                      >
                                        <VpnKey fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit System">
                                      <IconButton
                                        color="primary"
                                        onClick={() => handleEditSystem(system)}
                                        size="small"
                                        sx={{ mr: 1 }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete System">
                                      <IconButton
                                        color="error"
                                        onClick={() => deleteSystem(system.uuid)}
                                        size="small"
                                      >
                                        <Trash2 size={18} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {projectSystems.length === 0 && (
                        <EmptyState darkMode={darkMode} title="No systems found in this project" />
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Cameras Section */}
            <Grid item xs={12}>
              <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CameraIcon sx={{ color: '#16A987' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                        Cameras
                      </Typography>
                    </Box>
                    <Chip
                      label={`${projectCameras.length} Total`}
                      size="small"
                      sx={{ bgcolor: alpha('#16A987', 0.1), color: '#16A987', fontWeight: 600 }}
                    />
                  </Box>

                  {isMobile ? (
                    <Box>
                      <MobileSortControl
                        tableType="cameras"
                        currentConfig={sortConfig.cameras}
                        handleSort={handleSort}
                        options={[
                          { value: 'id', label: 'ID' },
                          { value: 'uuid', label: 'System UID' },
                          { value: 'camera_serial', label: 'Camera Serial' },
                          { value: 'status', label: 'Status' },
                          { value: 'registered_at', label: 'Registered At' },
                        ]}
                      />
                      {projectCameras.map((camera) => (
                        <Box
                          key={camera.id}
                          sx={{
                            p: 2, mb: 1.5, borderRadius: 2,
                            bgcolor: darkMode ? '#16213A' : '#EAF3FB',
                            border: darkMode ? '1px solid #24304A' : '1px solid #D8E1E8',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                              {camera.camera_serial}
                            </Typography>
                            <Chip
                              size="small"
                              label={camera.status}
                              sx={{
                                bgcolor: camera.status === 'online' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                                color: camera.status === 'online' ? 'success.main' : (darkMode ? '#93A2BC' : 'text.secondary'),
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#93A2BC' : 'text.secondary', fontFamily: 'monospace', mb: 1 }}>
                            {camera.uuid?.substring(0, 16)}...
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#93A2BC' : 'text.secondary', mb: 1.5 }}>
                            Registered {formatToIST(camera.registered_at)}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton color="primary" onClick={() => handleEditCamera(camera)} size="small">
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton color="error" onClick={() => deleteCamera(camera.id)} size="small">
                              <Trash2 size={18} />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                      {projectCameras.length === 0 && (
                        <EmptyState darkMode={darkMode} title="No cameras found in this project" />
                      )}
                    </Box>
                  ) : (
                    <>
                      <TableContainer>
                        <Table sx={{ minWidth: 800 }}>
                          <TableHead>
                            <TableRow>
                              {visibleColumns.cameras.id && (
                                <SortableHeader label="ID" tableType="cameras" columnKey="id" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                              )}
                              {visibleColumns.cameras.uuid && (
                                <SortableHeader label="System UID" tableType="cameras" columnKey="uuid" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                              )}
                              {visibleColumns.cameras.camera_serial && (
                                <SortableHeader label="Camera Serial" tableType="cameras" columnKey="camera_serial" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                              )}
                              {visibleColumns.cameras.status && (
                                <SortableHeader label="Status" tableType="cameras" columnKey="status" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                              )}
                              {visibleColumns.cameras.registered_at && (
                                <SortableHeader label="Registered At" tableType="cameras" columnKey="registered_at" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                              )}
                              {visibleColumns.cameras.actions && (
                                <TableCell align="center">Actions</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {projectCameras.map((camera) => (
                              <TableRow key={camera.id} hover>
                                {visibleColumns.cameras.id && (
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                      {camera.id}
                                    </Typography>
                                  </TableCell>
                                )}
                                {visibleColumns.cameras.uuid && (
                                  <TableCell>
                                    <Tooltip title={camera.uuid}>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                                        {camera.uuid.substring(0, 8)}...
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                )}
                                {visibleColumns.cameras.camera_serial && (
                                  <TableCell>
                                    <Typography variant="body2" sx={{ color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                      {camera.camera_serial}
                                    </Typography>
                                  </TableCell>
                                )}
                                {visibleColumns.cameras.status && (
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={camera.status}
                                      sx={{
                                        bgcolor: camera.status === 'online' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                                        color: camera.status === 'online' ? 'success.main' : (darkMode ? '#93A2BC' : 'text.secondary'),
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                )}
                                {visibleColumns.cameras.registered_at && (
                                  <TableCell>
                                    <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                                      {formatToIST(camera.registered_at)}
                                    </Typography>
                                  </TableCell>
                                )}
                                {visibleColumns.cameras.actions && (
                                  <TableCell align="center">
                                    <Tooltip title="Edit Camera">
                                      <IconButton
                                        color="primary"
                                        onClick={() => handleEditCamera(camera)}
                                        size="small"
                                        sx={{ mr: 1 }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Camera">
                                      <IconButton
                                        color="error"
                                        onClick={() => deleteCamera(camera.id)}
                                        size="small"
                                      >
                                        <Trash2 size={18} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {projectCameras.length === 0 && (
                        <EmptyState darkMode={darkMode} title="No cameras found in this project" />
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Licenses Section */}
            <Grid item xs={12}>
              <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VpnKey color={theme.palette.primary.main} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                        Licenses
                      </Typography>
                    </Box>
                    <Chip
                      label={`${projectLicenses.length} Total`}
                      size="small"
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }}
                    />
                  </Box>

                  {isMobile ? (
                    <Box>
                      <MobileSortControl
                        tableType="licenses"
                        currentConfig={sortConfig.licenses}
                        handleSort={handleSort}
                        options={[
                          { value: 'license_id', label: 'License ID' },
                          { value: 'uuid', label: 'System UID' },
                          { value: 'software_id', label: 'Software ID' },
                          { value: 'start_date', label: 'Start Date' },
                          { value: 'end_date', label: 'End Date' },
                          { value: 'status', label: 'Status' },
                        ]}
                      />
                      {projectLicenses.map((license) => {
                        const systemDetails = systems.find(s => s.uuid === license.uuid) || null;
                        const isExpired = new Date(license.end_date) < new Date();
                        const daysToExpire = Math.floor(
                          (new Date(license.end_date) - new Date()) / (1000 * 60 * 60 * 24)
                        );
                        return (
                          <Box
                            key={license.license_id}
                            sx={{
                              p: 2, mb: 1.5, borderRadius: 2,
                              bgcolor: darkMode ? '#16213A' : '#EAF3FB',
                              border: darkMode ? '1px solid #24304A' : '1px solid #D8E1E8',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                {license.license_id}
                              </Typography>
                              <Chip
                                size="small"
                                label={isExpired ? "Expired" : "Active"}
                                sx={{
                                  bgcolor: isExpired ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.success.main, 0.1),
                                  color: isExpired ? 'error.main' : 'success.main',
                                  fontWeight: 600,
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                              <Chip size="small" label={systemDetails?.software_id || 'N/A'} sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main', fontWeight: 600 }} />
                              {!isExpired && daysToExpire <= 30 && (
                                <Chip
                                  size="small"
                                  label={`${daysToExpire} days left`}
                                  sx={{
                                    bgcolor: daysToExpire <= 7 ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                                    color: daysToExpire <= 7 ? 'error.main' : 'warning.main',
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#93A2BC' : 'text.secondary', mb: 1.5 }}>
                              {license.start_date} → {license.end_date}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              {currentUser === "admin" && (
                                <IconButton color="primary" onClick={() => downloadLicenseFile(license.license_id)} size="small">
                                  <Download size={18} />
                                </IconButton>
                              )}
                              {currentUser === "admin" && (
                                <IconButton color="secondary" onClick={() => generateOfflineKey(license.license_id)} size="small">
                                  <Lock size={18} />
                                </IconButton>
                              )}
                              <IconButton color="error" onClick={() => deleteLicense(license.license_id)} size="small">
                                <Trash2 size={18} />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                      {projectLicenses.length === 0 && (
                        <EmptyState darkMode={darkMode} title="No licenses found in this project" />
                      )}
                    </Box>
                  ) : (
                    <>
                      <TableContainer>
                        <Table sx={{ minWidth: 1000 }}>
                          <TableHead>
                            <TableRow>
                              {visibleColumns.licenses.license_id && (
                                <SortableHeader label="License ID" tableType="licenses" columnKey="license_id" currentConfig={sortConfig.licenses} handleSort={handleSort} />
                              )}
                              {visibleColumns.licenses.uuid && (
                                <SortableHeader label="System UID" tableType="licenses" columnKey="uuid" currentConfig={sortConfig.licenses} handleSort={handleSort} />
                              )}
                              {visibleColumns.licenses.software_id && (
                                <SortableHeader label="Software ID" tableType="licenses" columnKey="software_id" currentConfig={sortConfig.licenses} handleSort={handleSort} />
                              )}
                              {visibleColumns.licenses.start_date && (
                                <SortableHeader label="Start Date" tableType="licenses" columnKey="start_date" currentConfig={sortConfig.licenses} handleSort={handleSort} />
                              )}
                              {visibleColumns.licenses.end_date && (
                                <SortableHeader label="End Date" tableType="licenses" columnKey="end_date" currentConfig={sortConfig.licenses} handleSort={handleSort} />
                              )}
                              {visibleColumns.licenses.status && (
                                <SortableHeader label="Status" tableType="licenses" columnKey="status" currentConfig={sortConfig.licenses} handleSort={handleSort} />
                              )}
                              {visibleColumns.licenses.actions && (
                                <TableCell align="center">Actions</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {projectLicenses.map((license) => {
                              const systemDetails = systems.find(s => s.uuid === license.uuid) || null;
                              const isExpired = new Date(license.end_date) < new Date();
                              const daysToExpire = Math.floor(
                                (new Date(license.end_date) - new Date()) / (1000 * 60 * 60 * 24)
                              );

                              return (
                                <TableRow key={license.license_id} hover>
                                  {visibleColumns.licenses.license_id && (
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                        {license.license_id}
                                      </Typography>
                                    </TableCell>
                                  )}
                                  {visibleColumns.licenses.uuid && (
                                    <TableCell>
                                      <Tooltip title={license.uuid}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                                          {license.uuid.substring(0, 8)}...
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                  )}
                                  {visibleColumns.licenses.software_id && (
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={systemDetails?.software_id || 'N/A'}
                                        sx={{
                                          bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                          color: 'secondary.main',
                                          fontWeight: 600,
                                        }}
                                      />
                                    </TableCell>
                                  )}
                                  {visibleColumns.licenses.start_date && (
                                    <TableCell>
                                      <Typography variant="body2" sx={{ color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                        {license.start_date}
                                      </Typography>
                                    </TableCell>
                                  )}
                                  {visibleColumns.licenses.end_date && (
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                          {license.end_date}
                                        </Typography>
                                        {!isExpired && daysToExpire <= 30 && (
                                          <Chip
                                            size="small"
                                            label={`${daysToExpire} days`}
                                            sx={{
                                              bgcolor: daysToExpire <= 7 ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                                              color: daysToExpire <= 7 ? 'error.main' : 'warning.main',
                                              fontWeight: 600,
                                              height: 20,
                                              '& .MuiChip-label': { px: 1, fontSize: '0.65rem' }
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </TableCell>
                                  )}
                                  {visibleColumns.licenses.status && (
                                    <TableCell>
                                      <Chip
                                        label={isExpired ? "Expired" : "Active"}
                                        size="small"
                                        sx={{
                                          bgcolor: isExpired ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.success.main, 0.1),
                                          color: isExpired ? 'error.main' : 'success.main',
                                          fontWeight: 600,
                                        }}
                                      />
                                    </TableCell>
                                  )}
                                  {visibleColumns.licenses.actions && (
                                    <TableCell align="center">
                                      {currentUser === "admin" && (
                                        <Tooltip title="Download .lic File">
                                          <IconButton
                                            color="primary"
                                            onClick={() => downloadLicenseFile(license.license_id)}
                                            size="small"
                                            sx={{ mr: 1 }}
                                          >
                                            <Download size={18} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {currentUser === "admin" && (
                                        <Tooltip title="Generate Offline Activation Key">
                                          <IconButton
                                            color="secondary"
                                            onClick={() => generateOfflineKey(license.license_id)}
                                            size="small"
                                            sx={{ mr: 1 }}
                                          >
                                            <Lock size={18} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip title="Delete License">
                                        <IconButton
                                          color="error"
                                          onClick={() => deleteLicense(license.license_id)}
                                          size="small"
                                        >
                                          <Trash2 size={18} />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {projectLicenses.length === 0 && (
                        <EmptyState darkMode={darkMode} title="No licenses found in this project" />
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    </Fade>
  );
}

export default ProjectDetailsView;
